import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import { itIT } from '@mui/x-data-grid/locales';
import {
  Card,
  Container,
  Grid,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../../utils/Buttons';
import { LoadingUI } from '../../../utils/LayoutStyles';
import { ErrorUI } from '../../../utils/ErrorHandler';

import UsersTable from './UsersTable';
import CardsTable from './CardsTable';

import API from '../../../api';
import ContactsTable from './ContactsTable';
import { usePopup } from '../../../context/PopupContext';

export default function DataImport() {
  const { showPopup, setPopUpText } = usePopup();

  const is600Screen = useMediaQuery('(min-width: 600px)');

  const [isImportLoading, setIsImportLoading] = useState(false);
  const [selectedImport, setSelectedImport] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [displayTable, setDisplayTable] = useState(false);
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [processedRows, setProcessedRows] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const errorColumns = [
    { field: 'fiscal_code', headerName: 'Codice Fiscale', editable: false, hideable: false, flex: 1 },
    { field: 'name', headerName: 'Nome', editable: false, hideable: false, flex: 1 },
    { field: 'surname', headerName: 'Cognome', editable: false, hideable: false, flex: 1 },
    { field: 'message', headerName: 'Errore', editable: false, flex: 1 },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarExport csvOptions={{ allColumns: true }} printOptions={{ disableToolbarButton: true }} />
      </GridToolbarContainer>
    );
  }

  const handleChangeSelectedImport = (event) => {
    setSelectedImport(event.target.value);
  };

  const [openUploadCSV, setOpenUploadCSV] = useState(false);

  const handleOpenUploadCSV = () => {
    setOpenUploadCSV(true);
  };

  const handleCloseUploadCSV = () => {
    setOpenUploadCSV(false);
    handleClearData(false);
    setIsImportLoading(false);
  };

  const handleClearData = (selectedImport) => {
    setFile(null);
    setDisplayTable(false);
    setUsersData([]);
    setCardsData([]);
    setErrorMessage('');
    setErrors([]);
    setProgress(0);
    setProcessedRows(0);
    setTotalRows(0);

    if (selectedImport) {
      setSelectedImport('');
    }
  };

  useEffect(() => {
    handleClearData(false);
  }, [selectedImport]);

  const [file, setFile] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [cardsData, setCardsData] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const fileReader = new FileReader();

  // CSV Reader
  useEffect(() => {
    if (file) {
      fileReader.onload = function (event) {
        const csvOutput = event.target.result;
        const csvArray = csvToArray(csvOutput);

        const requiredUserKeys = [
          'name',
          'surname',
          'birth_date',
          'place_of_birth',
          'fiscal_code',
          'entity_id',
          'email',
          'call_code',
          'phone',
          'res_equal_home',
          'res_street',
          'res_zip_code',
          'res_city_id',
          'res_city_name',
          'res_province_id',
          'res_region_name',
          'res_country_id',
          'home_street',
          'home_zip_code',
          'home_city_id',
          'home_city_name',
          'home_province_id',
          'home_region_name',
          'home_country_id',
          'position',
          'allow_news',
          'gd_check',
          'year_first_card',
          'cod_andromeda',
        ];

        const requiredCardKeys = [
          'campaign',
          'fiscal_code',
          'master_entity',
          'payment_type',
          'payment_status',
          'creation_date',
          'free_rate',
        ];

        const requiredContactKeys = ['email', 'entity_id'];

        let missingFields = [];
        let extraFields = [];

        if (csvArray.length > 0) {
          const fileKeys = Object.keys(csvArray[0]).map((key) => key.trim());

          if (selectedImport === 'user') {
            missingFields = requiredUserKeys.filter((key) => !fileKeys.includes(key));
            extraFields = fileKeys.filter((key) => !requiredUserKeys.includes(key));
          } else if (selectedImport === 'card') {
            missingFields = requiredCardKeys.filter((key) => !fileKeys.includes(key));
            extraFields = fileKeys.filter((key) => !requiredCardKeys.includes(key));
          } else {
            missingFields = requiredContactKeys.filter((key) => !fileKeys.includes(key));
          }
        }

        const isValidUserData = missingFields.length === 0 && extraFields.length === 0;
        const isValidCardData = missingFields.length === 0 && extraFields.length === 0;
        const isValidContactData = missingFields.length === 0;

        if (selectedImport === 'user' && isValidUserData) {
          setUsersData(csvArray);
        } else if (selectedImport === 'card' && isValidCardData) {
          setCardsData(csvArray);
        } else if (selectedImport === 'contact' && isValidContactData) {
          setContactsData(csvArray);
        } else {
          let errorMessage = "Il file non rispetta la formattazione richiesta per l'importazione dei dati.";

          if (missingFields.length > 0) {
            errorMessage += `\nCampi mancanti: ${missingFields.join(', ')}`;
          }
          if (extraFields.length > 0) {
            errorMessage += `\nCampi extra non previsti: ${extraFields.join(', ')}`;
          }

          handleCloseUploadCSV();
          setErrorMessage(errorMessage);
        }
      };

      fileReader.readAsText(file);
    }
  }, [file, selectedImport]);

  // CSV To Array Converter
  const csvToArray = (str, delimiter = ',') => {
    const headers = str
      .slice(0, str.indexOf('\n'))
      .split(delimiter)
      .map((header) => header.trim().replace(/\r/g, ''));

    const rows = str.slice(str.indexOf('\n') + 1).split('\n');

    const parseRow = (row) => {
      const values = [];
      let current = '';
      let insideQuotes = false;

      for (let i = 0; i < row.length; i += 1) {
        const char = row[i];
        const nextChar = row[i + 1];

        if (char === '"' && insideQuotes && nextChar === '"') {
          // Doppia doppia virgoletta ("") diventa una singola "
          current += '"';
          i += 1; // Salta il prossimo carattere
        } else if (char === '"') {
          insideQuotes = !insideQuotes; // Attiva/disattiva la modalità tra virgolette
        } else if (char === delimiter && !insideQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim()); // Aggiunge l'ultimo valore della riga

      return values;
    };

    const array = rows.map((row) => {
      const values = parseRow(row);

      const obj = headers.reduce((acc, header, i) => {
        acc[header] = values[i] || ''; // Evita undefined
        return acc;
      }, {});

      return obj;
    });

    return array.filter((obj) => Object.values(obj).some((value) => value !== '')); // Rimuove righe vuote
  };

  const handleOnFileChange = (e) => {
    const file = e.target.files[0];

    if (file && file.size > 2 * 1024 * 1024) {
      showPopup(false, 'Il file è troppo grande. Dimensione massima consentita: 2MB');
    } else {
      setFile(e.target.files[0]);
    }
  };

  const formatFileSize = (size) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleSubmitData = async (e) => {
    e.preventDefault();

    setIsImportLoading(true);
    setErrorMessage('');

    const chunkSize = 5;
    const data =
      selectedImport === 'user'
        ? usersData.map((user) => ({
            name: user.name,
            surname: user.surname,
            birth_date: user.birth_date,
            place_of_birth: user.place_of_birth,
            fiscal_code: user.fiscal_code,
            entity_id: user.entity_id,
            email: user.email,
            call_code: user.call_code,
            phone: user.phone,
            res_equal_home: user.res_equal_home,
            res_street: user.res_street,
            res_zip_code: user.res_zip_code,
            res_city_id: user.res_city_id,
            res_city_name: user.res_city_name,
            res_province_id: user.res_province_id,
            res_region_name: user.res_region_name,
            res_country_id: user.res_country_id,
            home_street: user.home_street,
            home_zip_code: user.home_zip_code,
            home_city_id: user.home_city_id,
            home_city_name: user.home_city_name,
            home_province_id: user.home_province_id,
            home_region_name: user.home_region_name,
            home_country_id: user.home_country_id,
            position_id: user.position,
            allow_news: user.allow_news,
            gd_check: user.gd_check,
            year_first_card: user.year_first_card,
            cod_andromeda: user.cod_andromeda,
          }))
        : selectedImport === 'card'
        ? cardsData.map((card) => ({
            campaign: card.campaign,
            fiscal_code: card.fiscal_code,
            master_entity: card.master_entity,
            payment_type: card.payment_type,
            payment_status: card.payment_status,
            creation_date: card.creation_date,
            free_rate: Number(card.free_rate).toFixed(2),
          }))
        : contactsData.map((contact) => ({
            name: contact.name,
            surname: contact.surname,
            birth_date: contact.birth_date,
            place_of_birth: contact.place_of_birth,
            fiscal_code: contact.fiscal_code,
            entity_id: contact.entity_id,
            email: contact.email,
            call_code: contact.call_code,
            phone: contact.phone,
            res_equal_home: contact.res_equal_home,
            res_street: contact.res_street,
            res_zip_code: contact.res_zip_code,
            res_city_id: contact.res_city_id,
            res_city_name: contact.res_city_name,
            res_province_id: contact.res_province_id,
            res_region_name: contact.res_region_name,
            res_country_id: contact.res_country_id,
            home_street: contact.home_street,
            home_zip_code: contact.home_zip_code,
            home_city_id: contact.home_city_id,
            home_city_name: contact.home_city_name,
            home_province_id: contact.home_province_id,
            home_region_name: contact.home_region_name,
            home_country_id: contact.home_country_id,
            position_id: contact.position,
            allow_news: contact.allow_news,
            gd_check: contact.gd_check,
            year_first_card: contact.year_first_card,
            cod_andromeda: contact.cod_andromeda,
          }));

    setTotalRows(data.length);

    const chunkArray = (arr, size) =>
      Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

    const dataChunks = chunkArray(data, chunkSize);
    const apiUrl =
      selectedImport === 'user' ? `/tempusers` : selectedImport === 'card' ? `/tempcards` : `/tempcontacts`;

    try {
      let allErrors = [];
      const totalChunks = dataChunks.length;
      let completedChunks = 0;
      let errorMessage;
      let successMessage;

      setIsUploadLoading(true);

      await dataChunks.reduce(
        (prevPromise, chunk, chunkIndex) =>
          prevPromise.then(() => {
            const baseIndex = chunkIndex * chunkSize;

            return API.post(apiUrl, chunk)
              .then((response) => {
                successMessage = response.data.message;

                if (selectedImport === 'contact' && response.data?.results) {
                  setContactsData((prev) => {
                    const updated = [...prev];

                    response.data.results.forEach((result, i) => {
                      const targetIndex = baseIndex + i;

                      if (updated[targetIndex]) {
                        updated[targetIndex] = {
                          ...updated[targetIndex],
                          z_message: result.message,
                        };
                      }
                    });

                    return updated;
                  });
                }
              })
              .catch((error) => {
                if (error.response?.data?.errors) {
                  allErrors = [...allErrors, ...error.response.data.errors];
                } else {
                  allErrors = [
                    ...allErrors,
                    {
                      fiscal_code: '-',
                      name: '-',
                      surname: '-',
                      message: error.response?.data?.message || "Errore durante l'importazione",
                    },
                  ];
                }

                if (error.response?.data?.message) {
                  errorMessage = error.response.data.message;
                } else {
                  errorMessage = "Errore durante l'importazione";
                }
              })
              .finally(() => {
                completedChunks += 1;
                setProcessedRows((prev) => prev + chunk.length);
                setProgress(Math.round((completedChunks / totalChunks) * 100));
              });
          }),
        Promise.resolve()
      );

      setIsImportLoading(false);
      setDisplayTable(true);
      setFile(null);
      setOpenUploadCSV(false);

      if (allErrors.length > 0) {
        setPopUpText(errorMessage);
        showPopup(false);
        setErrors(allErrors);
      } else {
        setPopUpText(successMessage?.length > 0 ? successMessage : 'Importazione completata con successo');
        showPopup(true);
      }
    } catch (error) {
      setPopUpText("Errore critico durante l'importazione");
      showPopup(false);
      setErrors([error.response?.data?.errors || 'Errore critico']);
    } finally {
      setIsUploadLoading(false);
      setProgress(100);
    }
  };

  const handleClickBack = (e) => {
    e.preventDefault();
    handleClearData(true);
    setSelectedImport('');
  };

  return (
    <>
      <Helmet>
        <title>Importazione</title>
      </Helmet>

      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack
              direction={is600Screen ? 'row' : 'column'}
              justifyContent={is600Screen ? 'space-between' : 'start'}
              alignItems={is600Screen ? 'center' : 'start'}
              gap={is600Screen ? '0' : '8px'}
            >
              <FormControl
                sx={{
                  width: '200px',
                  '& label.Mui-focused': {
                    color: '#212b36',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#212b36',
                    },
                  },
                }}
                size="small"
              >
                <InputLabel id="select-label">Seleziona Import</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedImport}
                  label="Seleziona Import"
                  onChange={handleChangeSelectedImport}
                >
                  <MenuItem value="user">Utenti</MenuItem>
                  <MenuItem value="contact">Contatti</MenuItem>
                  <MenuItem value="card">Tessere</MenuItem>
                </Select>
              </FormControl>

              <Grid item xs={12}>
                <Dialog open={openUploadCSV} onClose={() => {}} fullWidth>
                  <DialogTitle>
                    {isUploadLoading ? 'Upload in esecuzione' : 'Seleziona il file da importare'}
                  </DialogTitle>

                  {isUploadLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: '12px', padding: '16px 24px' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                        <Typography>Elementi processati: {processedRows}</Typography>
                        <Typography>Elementi da processare: {totalRows - processedRows}</Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexGrow: 1,
                        }}
                      >
                        <CircularProgress variant="determinate" value={progress} />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{ color: 'text.secondary' }}
                          >{`${progress}%`}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <DialogContent>
                        {file === null ? (
                          <form>
                            <Button
                              sx={{
                                height: '200px',
                                width: '100%',
                                boxShadow: 'none',
                                backgroundColor: '#f4f6f8',
                                color: 'black',
                                '&:hover': {
                                  backgroundColor: '#f4f6f8',
                                },
                              }}
                              variant="contained"
                              component="label"
                            >
                              Upload File
                              <input
                                type={'file'}
                                id={'csvFileInput'}
                                accept={'.csv'}
                                onChange={handleOnFileChange}
                                hidden
                              />
                            </Button>
                          </form>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              component="img"
                              src={'/assets/icons/ic_csv.svg'}
                              sx={{ width: 48, height: 48, flexShrink: 0 }}
                            />

                            <Stack direction="column" sx={{ minWidth: 240, flexGrow: 1 }}>
                              <Typography color="inherit" variant="subtitle2" noWrap>
                                {file.name}
                              </Typography>

                              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                                Dimensione file: {formatFileSize(file.size)}
                              </Typography>

                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Ultima modifica: {dayjs(file.lastModifiedDate).format('DD/MM/YYYY')}
                              </Typography>
                            </Stack>
                          </Stack>
                        )}
                      </DialogContent>
                      <DialogActions>
                        <DefaultOutlinedButton onClick={handleCloseUploadCSV}>Annulla</DefaultOutlinedButton>
                        {file !== null && (
                          <DefaultContainedButton onClick={handleSubmitData}>Importa Dati</DefaultContainedButton>
                        )}

                      </DialogActions>
                    </>
                  )}
                </Dialog>

                <Stack direction="row" alignItems="center" justifyContent="end" gap={1.5}>
                  <DefaultContainedButton
                    disabled={selectedImport === '' || displayTable}
                    startIcon={<FileUploadIcon />}
                    onClick={handleOpenUploadCSV}
                  >
                    Seleziona File
                  </DefaultContainedButton>

                  <DefaultContainedButton
                    disabled={!displayTable && errors.length === 0}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleClickBack}
                  >
                    Indietro
                  </DefaultContainedButton>
                </Stack>
              </Grid>
            </Stack>
          </Grid>

          {errors.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ padding: '24px' }}>
                {errorMessage === '' ? (
                  !displayTable ? (
                    isImportLoading ? (
                      LoadingUI()
                    ) : (
                      <Typography sx={{ textAlign: 'center' }}>Selezionare un file da importare</Typography>
                    )
                  ) : selectedImport === 'user' ? (
                    <UsersTable usersData={usersData} />
                  ) : selectedImport === 'card' ? (
                    <CardsTable cardsData={cardsData} />
                  ) : (
                    <ContactsTable contactsData={contactsData} />
                  )
                ) : (
                  ErrorUI(errorMessage)
                )}
              </Card>
            </Grid>
          )}
        </Grid>

        {errors.length > 0 && (
          <Card sx={{ padding: '24px', marginTop: '24px' }}>
            <Typography variant="h6" sx={{ marginBottom: '12px' }}>
              Errori riscontrati:
            </Typography>

            <DataGrid
              disableRowSelectionOnClick
              disableColumnSelector
              rows={errors}
              columns={errorColumns}
              slots={{ toolbar: CustomToolbar }}
              getRowId={(row, index) => row.fiscal_code + index}
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              pageSizeOptions={[10]}
              initialState={{
                ...errors.initialState,
                pagination: { paginationModel: { pageSize: 10 } },

                columns: {
                  ...errors.initialState?.columns,
                },
              }}
            />

          </Card>
        )}
      </Container>
    </>
  );
}
